import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mountain, Target, Zap, Shield, Users, Code2, ArrowUpRight } from "lucide-react";

const MISSION_IMG = "https://static.prod-images.emergentagent.com/jobs/e15ee57d-fd69-49a2-a17c-c96c4cdf8289/images/4e146597bb6cd32ddd2fddea814b48dad33f67b2bf85ae05985a92206f56a0b3.png";

const values = [
  { icon: <Zap size={22}/>, title: "Velocity is a deliverable.", desc: "We ship in weeks because time compounds. Faster shipping = faster learning = better products." },
  { icon: <Shield size={22}/>, title: "Security isn't a bolt-on.", desc: "Every system we build passes pen-test and compliance review. Zero trust as a default, not a feature." },
  { icon: <Target size={22}/>, title: "Taste over templates.", desc: "AI accelerates — but senior engineering curates. No generic SaaS output. No slop." },
  { icon: <Users size={22}/>, title: "Founders, not ticket-takers.", desc: "Every engagement gets a senior lead. You talk to people who've shipped billion-request systems." },
];

const team = [
  { name: "Alok G", role: "Founder", bio: "Sets the Ridgeline thesis: compress quarters into weeks without compromising quality. Leads every engagement architecture review and client strategy." },
  { name: "Kiran Tripathi", role: "Development Lead", bio: "Owns delivery across the studio. Architects the event-driven backbones and enforces the review bar that keeps the AI-hybrid pipeline honest." },
  { name: "Poonam Mishra", role: "Sr. Developer", bio: "Builds the interiors where speed meets correctness — from multi-tenant SaaS to security-hardened consoles. Ships under pressure, ships clean." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section className="relative border-b border-white/10 bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 topo-grid opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <p className="overline">About</p>
            </div>
            <div className="md:col-span-8">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-[-0.03em] leading-[0.95]" data-testid="about-title">
                A studio for teams who<br/>
                <span className="text-[#ff3333]">can't afford to wait.</span>
              </h1>
              <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
                Ridgeline Cyber Services LLP — RidgecX — is a hybrid software studio founded on a simple thesis: <span className="text-white">senior engineers paired with a disciplined AI toolchain ship enterprise-grade software 5x faster than traditional teams, without compromise.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION — split */}
      <section className="py-24 md:py-32" data-testid="about-mission">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:col-span-6 relative"
          >
            <div className="aspect-[4/5] overflow-hidden bg-black border border-white/10">
              <img src={MISSION_IMG} alt="Ridgeline" className="w-full h-full object-cover opacity-80" />
            </div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#ff3333] -mb-6 -ml-6 hidden md:block" />
          </motion.div>

          <div className="md:col-span-6">
            <div className="flex items-center gap-3 text-[#ff3333]">
              <Mountain size={22}/>
              <span className="overline text-[#ff3333]">Our mission</span>
            </div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
              Compress quarters into weeks —<br/>
              <span className="text-[#ff3333]">without compressing quality.</span>
            </h2>
            <div className="mt-8 space-y-5 text-zinc-400 leading-relaxed">
              <p>
                Software projects die in the gap between ambition and delivery. The timeline stretches. The spec drifts. The money runs out. The opportunity disappears.
              </p>
              <p>
                RidgecX exists to collapse that gap. We've rebuilt the delivery pipeline from the ground up around a hybrid model — senior engineers own architecture and review; AI executes the interior. The result is speed without shortcuts, and discipline without bureaucracy.
              </p>
              <p className="text-white">
                We don't sell sprints. We sell summits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#0a0a0a] border-y border-white/10 py-24 md:py-32" data-testid="about-values">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-4">
              <p className="overline">Principles</p>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
                Four non-negotiables.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="card-brutal"
                data-testid={`about-value-${i}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 border border-white/10 text-[#ff3333]">{v.icon}</div>
                  <h3 className="font-display text-xl font-bold text-white">{v.title}</h3>
                </div>
                <p className="mt-4 text-zinc-400 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 border-b border-white/10" data-testid="about-stats">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { v: "20+", l: "Enterprise projects · 90 days" },
            { v: "15+", l: "Products developed" },
            { v: "100%", l: "Before-time delivery" },
            { v: "17", l: "Senior engineers on staff" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-5xl sm:text-6xl font-black text-white tracking-tight">{s.v}</div>
              <div className="mt-2 overline">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 md:py-32" data-testid="about-team">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-4">
              <p className="overline">The team</p>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
                Senior operators.<br/>
                <span className="text-[#ff3333]">On every engagement.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((m, i) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="card-brutal group"
                data-testid={`about-team-${i}`}
              >
                <div className="aspect-square bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/5 mb-6 flex items-center justify-center text-[#ff3333]">
                  <Code2 size={40} strokeWidth={1.2} className="opacity-60 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="overline text-[#ff3333]">{m.role}</p>
                <h3 className="mt-2 font-display text-lg font-bold text-white">{m.name}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{m.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] border-t border-white/10 py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h2 className="font-display text-3xl md:text-4xl font-black text-white tracking-tight leading-tight max-w-2xl">
            Building something ambitious? <span className="text-[#ff3333]">Let's talk.</span>
          </h2>
          <Link to="/contact" className="btn-primary" data-testid="about-cta">
            Book a 30-min call <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
